import { DateTime } from 'luxon';
import { forecastForRange } from '/imports/api/productionForecast';
import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { GmapsAddress } from '/imports/gmaps';
import { numYearsBetween } from '/imports/utils';

export interface ProductionMeter extends Doc {
  orgId: string;
  name: string;
  identifier: string;
  type: 'alsoEnergy' | 'solarEdge' | 'powerhub' | 'enphase' | 'egauge' | 'sma' | 'smaClient' | 'solis' | 'fomware' | 'dummy';
  billingAccountNumber?: string;
  billingEmail?: string;
  customerName?: string;
  address?: GmapsAddress,
  formattedAddressOverride?: string; // manual bill service-address, used when `address` has no formatted string
  timezone?: string,
  commissioningDate?: Date;
  size?: number;
  chartColor?: string;
  ppaRate?: number;
  ppaEscalator?: number;
  fixedPayment?: number;
  forecastedProduction?: number[];
  degradationRate?: number;
}
export type Model = ProductionMeter;

export const ProductionMeters = new Mongo.Collection<ProductionMeter>('productionMeters');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: ProductionMeters,
  editableFields: ['active', 'orgId', 'name', 'type', 'timezone', 'commissioningDate', 'size', 'chartColor',
    'ppaRate', 'ppaEscalator', 'fixedPayment', 'degradationRate', 'forecastedProduction', 'address', 'formattedAddressOverride',
    'billingAccountNumber', 'billingEmail', 'customerName', 'identifier',
  ],
  fieldLimits: {
    'billingAccountNumber': 1024, 'billingEmail': 320, 'chartColor': 32, 'customerName': 256, 'formattedAddressOverride': 256,
    'identifier': 1024, 'name': 256, 'orgId': 64, 'timezone': 64, 'type': 64,
  },
  isOrgOwned: true,
  listMethod: DeclareMethod<ListMethodArg<ProductionMeter>, ListMethodResponse<ProductionMeter, Refs>>('listProductionMeters'),
  getMethod: DeclareMethod<string, GetMethodResponse<ProductionMeter, Refs>>('getProductionMeter'),
  createMethod: DeclareMethod<Partial<ProductionMeter>, string>('createProductionMeter'),
  updateMethod: DeclareMethod<Partial<ProductionMeter>, string>('updateProductionMeter'),
  deleteMethod: DeclareMethod<string>('deleteProductionMeter'),
  pathPrefix: '/production-meters',
  asString: (document) => document.name || document.identifier || document._id,
  modelName: strings.ProductionMeter(),
  pluralName: strings.ProductionMeters(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
        { identifier: new RegExp(search, 'gi') },
        { billingAccountNumber: new RegExp(search, 'gi') },
        { type: search as ProductionMeter['type'] },
      ]
    };
  },
  validate: async (project: Partial<ProductionMeter>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, project, 'active');
    validateString(errors, project, 'orgId', { required: true });
    validateString(errors, project, 'name', { required: true });
    validateString(errors, project, 'identifier', { required: true });
    validateString(errors, project, 'type', { required: true });
    return errors;
  },
};


export interface ProductionMeterProductionReportRow {
  meterId: string;
  name: string;
  production1day: number;
  production7days: number;
  estimated7days: number;
  lastIntervalTime?: Date;
  dailyProduction: { date: string, production: number }[];
  gaps: any[];
  state?: string;
  orgId: string;
  orgName: string;
}
export const getProductionMeterProductionReport = DeclareMethod<void, ProductionMeterProductionReportRow[]>('getProductionMeterProductionReport');

export interface ProductionMeterMetaReportRow {
  meterId: string;
  name: string;
  orgId: string;
  orgName: string;
  address?: string;
  timezone?: string;
  ppaRate?: number;
  fixedPayment?: number;
  forecastLen?: number;
  billingAccountNumber?: string;
  commissioningDate?: Date;
  degradationRate?: number;
  ppaEscalator?: number;
  size?: number;
  missingFields: string[];
}
export const getProductionMeterMetaReport = DeclareMethod<void, ProductionMeterMetaReportRow[]>('getProductionMeterMetaReport');

Meteor.isServer && Meteor.startup(async () => {
  await ProductionMeters.createIndexAsync({ orgId: 1 });
  await ProductionMeters.createIndexAsync({ name: 1 });
  await ProductionMeters.createIndexAsync({ identifier: 1 });
  await ProductionMeters.createIndexAsync({ type: 1 });
});

/**
 * Human-readable label for a meter's integration type.
 * @param meter - the production meter
 * @returns display label, or '' when no meter
 */
export const productionMeterType = (meter?: ProductionMeter) => {
  if (!meter) return '';
  if (meter.type === 'alsoEnergy') return 'AlsoEnergy';
  if (meter.type === 'egauge') return 'EGauge';
  if (meter.type === 'enphase') return 'Enphase';
  if (meter.type === 'powerhub') return 'Powerhub';
  if (meter.type === 'solarEdge') return 'SolarEdge';
  if (meter.type === 'sma') return 'SMA';
  if (meter.type === 'smaClient') return 'SMA Client';
  if (meter.type === 'solis') return 'Solis';
  if (meter.type === 'fomware') return 'FOMware';
  if (meter.type === 'dummy') return 'Dummy';
  return meter.type;
};

/**
 * PPA rate for a meter at a given date, applying the annual escalator.
 * @param meter - the production meter
 * @param date - rate date; defaults to now
 * @returns the escalated PPA rate
 */
export const productionMeterPpaRate = (meter: ProductionMeter, date?: Date) => {
  date = date || new Date();
  const { commissioningDate, ppaRate, ppaEscalator } = meter;
  const numYears = commissioningDate ? numYearsBetween(date, commissioningDate) : 0;
  return (ppaRate || 0) * (1 + numYears * (ppaEscalator || 0) / 100);
};

/**
 * Degradation-adjusted monthly production forecast from a reference date: the months of refDate's calendar year
 * from refDate's month on (that month only from refDate itself when it is this year — the caller has actuals up
 * to then), preceded — with wrapYear — by the months before it taken from next year, so the twelve values read
 * as the coming year. Values are in the order pushed, not calendar order.
 * @returns 12 monthly production values (fewer without wrapYear)
 */
export const productionMeterForecastedProduction = (meter: ProductionMeter, refDate: Date, wrapYear = false) =>
  forecastedYear(meter, refDate, wrapYear).map((f) => f.production);

/** Revenue counterpart of productionMeterForecastedProduction — degradation and PPA escalation applied. */
export const productionMeterForecastedRevenue = (meter: ProductionMeter, refDate: Date, wrapYear = false) =>
  forecastedYear(meter, refDate, wrapYear).map((f) => f.revenue);

const forecastedYear = (meter: ProductionMeter, refDate: Date, wrapYear: boolean) => {
  const ref = DateTime.fromJSDate(refDate || new Date(), { zone: meter.timezone });
  const startOfYear = ref.startOf('year');
  const startOfNextYear = startOfYear.plus({ years: 1 });
  const refMonth = ref.month - 1;
  const isCurrentYear = ref.year === DateTime.now().setZone(meter.timezone).year;
  const out: { production: number; revenue: number }[] = [];
  for (let i = 0; i < refMonth; i++) {
    if (!wrapYear) continue;
    const monthStart = startOfNextYear.plus({ months: i });
    out.push(productionMeterForecastForRange(meter, monthStart, monthStart.plus({ months: 1 })));
  }
  for (let i = refMonth; i < 12; i++) {
    const monthStart = startOfYear.plus({ months: i });
    const start = i === refMonth && isCurrentYear && ref > monthStart ? ref : monthStart;
    out.push(productionMeterForecastForRange(meter, start, monthStart.plus({ months: 1 })));
  }
  return out;
};

/** Forecast production and revenue between two instants — see forecastForRange. */
export const productionMeterForecastForRange = (meter: ProductionMeter, start: DateTime, end: DateTime) => forecastForRange(meter, start, end);

/**
 * Degradation- and escalator-adjusted production and revenue forecast for a single month.
 * @param monthKey - target month as 'yyyy-MM'
 * @param opts.fromNow - default true: the CURRENT month counts only from now, for the views that blend actuals
 *   with forecast (YTD / Quarterly). Pass false for a pure forecast, which must not move with the clock.
 * @returns { production, revenue } for that month
 */
export const productionMeterForecastForMonth = (meter: ProductionMeter, monthKey: string, opts: { fromNow?: boolean } = {}) => {
  const monthStart = DateTime.fromFormat(`${monthKey}-01`, 'yyyy-MM-dd', { zone: meter.timezone });
  const monthEnd = monthStart.plus({ months: 1 });
  const now = DateTime.now().setZone(meter.timezone);
  const start = opts.fromNow !== false && now > monthStart && now < monthEnd ? now : monthStart;
  return productionMeterForecastForRange(meter, start, monthEnd);
};